import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, Ban, Bot, Check, ChevronLeft, Clock, Coins, Flag, Loader2, Play, RotateCcw, Scale, Send, Settings2, User, Wrench, X,
} from 'lucide-react';
import {
  KB_SWEEP_FINDING_KIND_DESCRIPTIONS,
  KB_SWEEP_FINDING_KIND_LABELS,
  KB_SWEEP_FINDING_KINDS,
  KB_SWEEP_FINDING_STATUS_LABELS,
  KB_SWEEP_SEVERITY_LABELS,
  formatRunCost,
  formatRunTokens,
  runUsageTokens,
} from '@repo/shared-types';
import { ScopeBar } from '../components/scope/ScopeBar';
import { RunPhaseHeader, RunTimelineView } from '../components/run/RunActivity';
import { RunCostChip, describeRunUsage, runUsageOf } from '../components/run/RunCost';
import { Badge } from '../components/ui/badge';
import { BreathingDot, SweepSurface } from '../components/ui/breathing';
import { Button } from '../components/ui/button';
import { Dialog } from '../components/ui/dialog';
import { Input, Select, Textarea } from '../components/ui/input';
import { Markdown, compactMarkdownComponents } from '../components/ui/markdown';
import { ResizeHandle } from '../components/ui/resize-handle';
import {
  kbSweepsApi,
  type KbSweep,
  type KbSweepCounts,
  type KbSweepFinding,
  type KbSweepFindingDetail,
  type KbSweepFindingKind,
  type KbSweepFindingStatus,
  type KbSweepSettings,
  type KbSweepSeverity,
  type KbSweepUsageTotal,
  type RunLifecycleStatus,
  type RunUsage,
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { useResizablePanel } from '../lib/resizable';
import { useLiveHeadline, useRunStream } from '../lib/run-stream';
import { useScope } from '../lib/scope';
import { cn, formatDate, timeAgo } from '../lib/utils';

/**
 * KB Sweeps: an agent reads the store and says what is wrong with how it is shaped; a person
 * answers each card, and the agent fixes it through the ordinary ingestion door.
 *
 * Two panes with a drag handle between them — the shell Ask KB and the review queue share. The
 * left is the worklist, in two views: the **sweeps** (each one an agent run, watchable while it
 * moves, with its report and its cards when it is done) and the **cards** across every sweep, open
 * first. The right is one thing in full: a sweep with its live timeline and report, or a card with
 * its evidence, its thread and the box where you tell the agent what to do.
 *
 * The three controls that set an agent running or writing — Sweep now, the daily schedule, and the
 * message box on a card — sit behind `KB_QUALITY_SWEEP`; reading, dismissing and resolving do not.
 */

const LIVE: ReadonlySet<string> = new Set(['QUEUED', 'PROCESSING']);
const POLL_MS = 4_000;

const SEVERITY_VARIANT: Record<KbSweepSeverity, 'danger' | 'warning' | 'muted'> = { HIGH: 'danger', MEDIUM: 'warning', LOW: 'muted' };
const STATUS_VARIANT: Record<KbSweepFindingStatus, 'default' | 'info' | 'success' | 'muted'> = {
  OPEN: 'default',
  FIXING: 'info',
  FIXED: 'success',
  RESOLVED: 'success',
  DISMISSED: 'muted',
};
const SWEEP_STATUS_VARIANT: Record<KbSweep['status'], 'muted' | 'info' | 'success' | 'danger'> = {
  QUEUED: 'muted',
  PROCESSING: 'info',
  COMPLETED: 'success',
  FAILED: 'danger',
};

function elapsed(startedAt: string | null, finishedAt: string | null): string | null {
  if (!startedAt) return null;
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  const s = Math.max(0, Math.round((end - new Date(startedAt).getTime()) / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

function sweepTally(sweep: KbSweep): string {
  return `${sweep.findingsFiled} flagged · ${sweep.findingsConfirmed} confirmed · ${sweep.findingsResolved} withdrawn`;
}

export default function KbSweeps() {
  const scope = useScope();
  const { hasCapability } = useAuth();
  const canRun = hasCapability('KB_QUALITY_SWEEP');
  const projectId = scope.resolvedProjectId;
  const [searchParams, setSearchParams] = useSearchParams();

  const [pane, setPane] = useState<'sweeps' | 'cards'>(searchParams.get('card') ? 'cards' : 'sweeps');
  const [sweeps, setSweeps] = useState<KbSweep[]>([]);
  const [counts, setCounts] = useState<KbSweepCounts | null>(null);
  const [cards, setCards] = useState<KbSweepFinding[]>([]);
  const [status, setStatus] = useState<KbSweepFindingStatus | 'ALL' | 'LIVE'>('LIVE');
  const [severity, setSeverity] = useState<KbSweepSeverity | ''>('');
  const [kind, setKind] = useState<KbSweepFindingKind | ''>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [instruction, setInstruction] = useState('');
  const [starting, setStarting] = useState(false);
  const [settings, setSettings] = useState<KbSweepSettings | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  /** What the right pane shows. Held in the URL so a card can be linked to. */
  const selected = useMemo<{ kind: 'sweep' | 'finding'; id: string } | null>(() => {
    const card = searchParams.get('card');
    if (card) return { kind: 'finding', id: card };
    const sweep = searchParams.get('sweep');
    if (sweep) return { kind: 'sweep', id: sweep };
    return null;
  }, [searchParams]);
  const select = useCallback(
    (next: { kind: 'sweep' | 'finding'; id: string } | null) => {
      const params = new URLSearchParams(searchParams);
      params.delete('sweep');
      params.delete('card');
      if (next) params.set(next.kind === 'sweep' ? 'sweep' : 'card', next.id);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const listPanel = useResizablePanel({ initial: 380, min: 300, max: 560, side: 'left', storageKey: 'kb.sweeps.list.width' });

  // ─── Loading ────────────────────────────────────────────────────────────────

  const loadSweeps = useCallback(async () => {
    if (!projectId) return;
    try {
      const [rows, summary] = await Promise.all([kbSweepsApi.list(projectId, { scope: scope.mode, limit: 40 }), kbSweepsApi.summary(projectId, scope.mode)]);
      setSweeps(rows);
      setCounts(summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [projectId, scope.mode]);

  const loadCards = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const result = await kbSweepsApi.findings(projectId, {
        scope: scope.mode,
        status: status === 'LIVE' ? undefined : status,
        severity: severity || undefined,
        kind: kind || undefined,
        limit: 200,
      });
      setCards(result.findings);
      setCounts((prev) => (prev ? { ...prev, ...result.counts } : { ...result.counts, liveSweeps: 0 }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [projectId, scope.mode, status, severity, kind]);

  useEffect(() => {
    setError(null);
    void loadSweeps();
  }, [loadSweeps]);

  useEffect(() => {
    if (pane === 'cards') void loadCards();
  }, [pane, loadCards]);

  useEffect(() => {
    if (!projectId) return;
    kbSweepsApi.settings(projectId).then(setSettings).catch(() => setSettings(null));
  }, [projectId]);

  // A moving sweep keeps the list alive; a quiet page makes no requests.
  const anyLive = sweeps.some((s) => LIVE.has(s.status));
  useEffect(() => {
    if (!anyLive) return;
    const timer = window.setInterval(() => void loadSweeps(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [anyLive, loadSweeps]);

  // ─── Actions ────────────────────────────────────────────────────────────────

  async function act(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function startSweep() {
    if (!projectId) return;
    setStarting(true);
    await act(async () => {
      const sweep = await kbSweepsApi.start(projectId, instruction);
      setInstruction('');
      setPane('sweeps');
      await loadSweeps();
      select({ kind: 'sweep', id: sweep.id });
    });
    setStarting(false);
  }

  const subtitle = scope.isEverything
    ? 'An agent reads the whole store and flags what is misplaced, duplicated, contradictory or badly shaped. Tell it what to do about each card; it fixes through the ingestion pipeline and closes the card itself.'
    : `Sweeping ${scope.project?.name ?? 'this project'}${scope.mode === 'cone' ? ' and everything beneath it' : ' alone'}.`;

  return (
    <div className="flex h-screen overflow-hidden bg-surface">
      <div className="pl-[var(--sidebar-w)] pr-[var(--rail-w)] flex-1 h-full flex overflow-hidden">
        <aside style={{ width: listPanel.width }} className="flex-shrink-0 h-full flex flex-col border-r border-border bg-surface-subtle/40">
          <div className="flex items-center gap-1 border-b border-border-subtle px-4 pt-4 pb-3">
            {(['sweeps', 'cards'] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setPane(value)}
                className={cn('rounded-md px-2.5 py-1 text-xs font-medium transition-colors', pane === value ? 'bg-surface-muted text-ink' : 'text-ink-muted hover:text-ink')}
              >
                {value === 'sweeps' ? 'Sweeps' : 'Cards'}
                {value === 'cards' && counts && counts.total > 0 && (
                  <span className={cn('ml-1.5 text-[10px] tabular-nums', counts.high > 0 ? 'text-danger-text' : 'text-ink-faint')}>{counts.total}</span>
                )}
              </button>
            ))}
            {counts && (
              <span className="ml-auto text-[10px] text-ink-faint tabular-nums" title="Open cards by severity">
                <span className={counts.high > 0 ? 'text-danger-text' : ''}>{counts.high} high</span> · {counts.medium} · {counts.low}
              </span>
            )}
          </div>

          {counts?.spend && (counts.spend.sweeps.runs > 0 || counts.spend.fixes.runs > 0) && <SpendLine spend={counts.spend} />}

          {pane === 'sweeps' ? (
            <>
              {canRun && (
                <div className="px-4 pt-3 pb-3 border-b border-border-subtle space-y-2">
                  <Textarea
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    rows={2}
                    placeholder="Optional: what to attend to — “focus on the Sales engagements”, “look for Heist pages filed elsewhere”"
                    className="w-full text-xs"
                  />
                  <div className="flex items-center gap-2">
                    <Button variant="primary" size="sm" onClick={startSweep} disabled={!projectId || starting || (counts?.liveSweeps ?? 0) > 0} title={(counts?.liveSweeps ?? 0) > 0 ? 'A sweep is already running in this scope' : 'Read the whole scope now'}>
                      {starting ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
                      Sweep now
                    </Button>
                    <button type="button" onClick={() => setSettingsOpen((v) => !v)} className={cn('ml-auto inline-flex items-center gap-1 text-[11px]', settings?.sweepMode === 'DAILY' ? 'text-orange-600' : 'text-ink-muted hover:text-ink')} title="The daily schedule">
                      <Settings2 size={12} />
                      {settings?.sweepMode === 'DAILY' ? `Daily at ${settings.sweepTimeOfDay ?? '—'}` : 'Schedule off'}
                    </button>
                  </div>
                  {settingsOpen && projectId && settings && (
                    <SettingsPanel settings={settings} onSaved={(next) => setSettings(next)} projectId={projectId} onError={setError} />
                  )}
                </div>
              )}
              <div className="flex-1 overflow-y-auto px-3 py-3">
                {sweeps.length === 0 ? (
                  <div className="px-2 py-12 text-center text-[11px] text-ink-faint">
                    <Scale size={18} className="mx-auto mb-2 opacity-60" />
                    {canRun ? 'No sweeps yet. Press Sweep now, or switch the daily schedule on.' : 'No sweeps yet.'}
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {sweeps.map((sweep) => (
                      <SweepRow key={sweep.id} sweep={sweep} active={selected?.kind === 'sweep' && selected.id === sweep.id} onOpen={() => select({ kind: 'sweep', id: sweep.id })} />
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="px-4 pt-3 pb-3 border-b border-border-subtle space-y-2">
                <Select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} className="w-full py-1.5 text-xs">
                  <option value="LIVE">Open and being fixed</option>
                  <option value="OPEN">Open</option>
                  <option value="FIXING">Being fixed</option>
                  <option value="FIXED">Fixed</option>
                  <option value="RESOLVED">Resolved</option>
                  <option value="DISMISSED">Dismissed</option>
                  <option value="ALL">Everything</option>
                </Select>
                <div className="flex gap-2">
                  <Select value={severity} onChange={(e) => setSeverity(e.target.value as KbSweepSeverity | '')} className="flex-1 min-w-0 py-1.5 text-xs">
                    <option value="">Any severity</option>
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                  </Select>
                  <Select value={kind} onChange={(e) => setKind(e.target.value as KbSweepFindingKind | '')} className="flex-1 min-w-0 py-1.5 text-xs">
                    <option value="">Any kind</option>
                    {KB_SWEEP_FINDING_KINDS.map((k) => (
                      <option key={k} value={k}>{KB_SWEEP_FINDING_KIND_LABELS[k]}</option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-3 py-3">
                {loading && cards.length === 0 ? (
                  <p className="px-1 text-[11px] text-ink-faint">Loading…</p>
                ) : cards.length === 0 ? (
                  <div className="px-2 py-12 text-center text-[11px] text-ink-faint">
                    <Flag size={18} className="mx-auto mb-2 opacity-60" />
                    {status === 'LIVE' ? 'Nothing open. Run a sweep to look again.' : 'No cards match these filters.'}
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {cards.map((finding) => (
                      <FindingRow key={finding.id} finding={finding} active={selected?.kind === 'finding' && selected.id === finding.id} onOpen={() => select({ kind: 'finding', id: finding.id })} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </aside>

        <ResizeHandle handleProps={listPanel.handleProps} onDoubleClick={listPanel.reset} />

        <div className="flex-1 h-full overflow-y-auto min-w-0">
          <div className="px-7 py-7">
            <div className="flex items-start justify-between gap-6 mb-4">
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-ink">KB Sweeps</h1>
                <p className="mt-1 max-w-2xl text-xs text-ink-muted">{subtitle}</p>
              </div>
            </div>
            <ScopeBar scope={scope} className="mb-5" />

            {error && <div className="mb-5 rounded-md border border-danger-ring bg-danger-bg px-4 py-3 text-sm text-danger-text">{error}</div>}

            {selected?.kind === 'sweep' && projectId ? (
              <SweepPane
                key={selected.id}
                projectId={projectId}
                sweepId={selected.id}
                canRun={canRun}
                onOpenFinding={(id) => select({ kind: 'finding', id })}
                onChanged={loadSweeps}
                onError={setError}
              />
            ) : selected?.kind === 'finding' && projectId ? (
              <FindingPane
                key={selected.id}
                projectId={projectId}
                findingId={selected.id}
                canRun={canRun}
                projectSlugFor={(pid) => scope.projectFor(pid)?.slug}
                onBack={() => {
                  const from = searchParams.get('from');
                  if (from) select({ kind: 'sweep', id: from });
                  else select(null);
                }}
                onChanged={() => {
                  void loadSweeps();
                  if (pane === 'cards') void loadCards();
                }}
                onError={setError}
              />
            ) : (
              <div className="rounded-lg border border-border bg-card py-20 text-center text-ink-faint">
                <Scale size={20} className="mx-auto mb-2 opacity-60" />
                <p className="mx-auto max-w-md text-sm">
                  Pick a sweep on the left to watch it work and read what it found, or a card to see the evidence and tell the agent what to do about it.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Rows ───────────────────────────────────────────────────────────────────

function SweepRow({ sweep, active, onOpen }: { sweep: KbSweep; active: boolean; onOpen: () => void }) {
  const live = LIVE.has(sweep.status);
  return (
    <SweepSurface active={live} className={cn('rounded-md border bg-card transition-colors', active ? 'border-orange-400' : 'border-border hover:border-orange-300')}>
      <button type="button" onClick={onOpen} className="w-full px-2.5 py-2 text-left">
        <span className="mb-1 flex items-center gap-1.5">
          {live ? (
            <span className="inline-flex items-center gap-1.5">
              <BreathingDot size={7} />
              <span className="text-[11px] font-medium text-orange-600 dark:text-orange-400">{sweep.status === 'QUEUED' ? 'queued' : 'sweeping'}</span>
            </span>
          ) : (
            <Badge variant={SWEEP_STATUS_VARIANT[sweep.status]}>{sweep.status === 'COMPLETED' ? 'Done' : 'Failed'}</Badge>
          )}
          <span className="text-[10px] text-ink-faint">{sweep.trigger === 'SCHEDULED' ? 'daily' : 'by hand'}</span>
          <span className="ml-auto text-[10px] text-ink-faint whitespace-nowrap">{timeAgo(sweep.queuedAt)}</span>
        </span>
        <span className="block text-xs leading-snug text-ink-secondary line-clamp-2">
          {live ? sweep.activity ?? 'Starting…' : sweep.status === 'FAILED' ? sweep.errorMessage ?? 'Failed' : sweepTally(sweep)}
        </span>
        <span className="mt-1.5 flex items-center gap-2 text-[10px] text-ink-faint">
          <span className="truncate">{sweep.projectName ?? '—'}{sweep.instruction ? ` · “${sweep.instruction.slice(0, 40)}${sweep.instruction.length > 40 ? '…' : ''}”` : ''}</span>
          <span className="flex-1" />
          <RunCostChip usage={runUsageOf(sweep)} />
        </span>
      </button>
    </SweepSurface>
  );
}

function FindingRow({ finding, active, onOpen }: { finding: KbSweepFinding; active: boolean; onOpen: () => void }) {
  return (
    <div className={cn('rounded-md border bg-card transition-colors', active ? 'border-orange-400' : 'border-border hover:border-orange-300')}>
      <button type="button" onClick={onOpen} className="w-full px-2.5 py-2 text-left">
        <span className="mb-1 flex flex-wrap items-center gap-1.5">
          <code className="text-[10px] text-ink-muted">{finding.shortId}</code>
          <Badge variant={SEVERITY_VARIANT[finding.severity]}>{KB_SWEEP_SEVERITY_LABELS[finding.severity]}</Badge>
          <span className="text-[10px] text-ink-faint">{KB_SWEEP_FINDING_KIND_LABELS[finding.kind]}</span>
          {finding.status !== 'OPEN' && <Badge variant={STATUS_VARIANT[finding.status]}>{KB_SWEEP_FINDING_STATUS_LABELS[finding.status]}</Badge>}
        </span>
        <span className="block text-xs leading-snug text-ink-secondary line-clamp-2">{finding.title}</span>
        <span className="mt-1.5 flex items-center gap-2 text-[10px] text-ink-faint">
          <span className="truncate">{finding.pages.map((p) => p.address).join(' · ')}</span>
          <span className="flex-1" />
          {finding.fixUsage.costUsd > 0 && (
            <span className="flex-shrink-0 inline-flex items-center gap-1" title={`Fix runs: ${describeRunUsage(finding.fixUsage)}`}>
              <Wrench size={9} />
              <span className="tabular-nums">{formatRunCost(finding.fixUsage.costUsd)}</span>
            </span>
          )}
          {finding.messageCount > 0 && <span className="flex-shrink-0 tabular-nums" title="messages on the thread">{finding.messageCount} msg</span>}
          {finding.seenCount > 1 && <span className="flex-shrink-0 tabular-nums" title="sweeps that have seen it">×{finding.seenCount}</span>}
          <span className="flex-shrink-0 whitespace-nowrap" title={`First seen ${formatDate(finding.firstSeenAt)}`}>{timeAgo(finding.firstSeenAt)}</span>
        </span>
      </button>
    </div>
  );
}

// ─── One sweep ──────────────────────────────────────────────────────────────

function SweepPane({
  projectId, sweepId, canRun, onOpenFinding, onChanged, onError,
}: {
  projectId: string;
  sweepId: string;
  canRun: boolean;
  onOpenFinding: (id: string) => void;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const [detail, setDetail] = useState<{ sweep: KbSweep; findings: KbSweepFinding[]; fixUsage: KbSweepUsageTotal; totalUsage: RunUsage } | null>(null);
  const [, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      setDetail(await kbSweepsApi.get(projectId, sweepId));
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    }
  }, [projectId, sweepId, onError]);

  useEffect(() => {
    void load();
  }, [load]);

  const live = detail ? LIVE.has(detail.sweep.status) : false;
  useEffect(() => {
    if (!live) return;
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [live, load]);

  const { timeline, live: streaming, loading, error: streamError } = useRunStream(projectId, sweepId, (detail?.sweep.status as RunLifecycleStatus | undefined) ?? null, 'sweep');
  const headline = useLiveHeadline(timeline);

  if (!detail) return <p className="text-sm text-ink-faint">Loading…</p>;
  const { sweep, findings, fixUsage, totalUsage } = detail;

  const openCard = (id: string) => {
    // Remember where we came from so the card's back link returns here.
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      next.set('from', sweepId);
      return next;
    }, { replace: true });
    onOpenFinding(id);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={SWEEP_STATUS_VARIANT[sweep.status]}>{sweep.status}</Badge>
        <Badge variant="default">{sweep.trigger === 'SCHEDULED' ? 'Daily schedule' : 'Sweep now'}</Badge>
        {sweep.scope && <span className="text-xs text-ink-muted">{sweep.scope.length === 1 ? sweep.scope[0]!.name : `${sweep.scope.length} projects`}</span>}
        <span className="text-xs text-ink-faint">{formatDate(sweep.queuedAt)}{elapsed(sweep.startedAt, sweep.finishedAt) ? ` · ${elapsed(sweep.startedAt, sweep.finishedAt)}` : ''}</span>
        <span className="ml-auto flex items-center gap-2">
          <RunCostChip usage={runUsageOf(sweep)} />
          {canRun && live && (
            <Button
              variant="secondary"
              size="sm"
              disabled={sweep.cancelRequested}
              onClick={async () => {
                try {
                  await kbSweepsApi.cancel(projectId, sweepId);
                  await load();
                  onChanged();
                } catch (err) {
                  onError(err instanceof Error ? err.message : String(err));
                }
              }}
            >
              <Ban size={13} /> {sweep.cancelRequested ? 'Stopping…' : 'Cancel'}
            </Button>
          )}
        </span>
      </div>

      <SweepCostStrip sweep={sweep} fixUsage={fixUsage} totalUsage={totalUsage} live={live} />

      {sweep.instruction && (
        <div className="rounded-md border border-border bg-surface-subtle px-4 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-ink-faint mb-1">Instruction</p>
          <p className="text-xs text-ink-secondary whitespace-pre-wrap">{sweep.instruction}</p>
        </div>
      )}

      <div className="space-y-3">
        <RunPhaseHeader timeline={timeline} headline={headline} live={streaming} />
        {streamError && <p className="text-[11px] text-danger-text">{streamError}</p>}
        <RunTimelineView timeline={timeline} live={streaming} loading={loading} />
      </div>

      {sweep.report && (
        <section className="rounded-lg border border-border bg-card px-5 py-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-ink-faint mb-2">Report</p>
          <Markdown className="text-sm">{sweep.report}</Markdown>
        </section>
      )}

      <section>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-ink-faint">Cards from this sweep</p>
          <span className="text-[10px] text-ink-faint">{sweepTally(sweep)}</span>
        </div>
        {findings.length === 0 ? (
          <p className="text-xs text-ink-faint">{live ? 'Cards appear here as the agent files them.' : 'This sweep filed and confirmed nothing.'}</p>
        ) : (
          <div className="space-y-1.5">
            {findings.map((finding) => (
              <FindingRow key={finding.id} finding={finding} active={false} onOpen={() => openCard(finding.id)} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ─── One card ───────────────────────────────────────────────────────────────

function FindingPane({
  projectId, findingId, canRun, projectSlugFor, onBack, onChanged, onError,
}: {
  projectId: string;
  findingId: string;
  canRun: boolean;
  projectSlugFor: (projectId: string) => string | undefined;
  onBack: () => void;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const [detail, setDetail] = useState<KbSweepFindingDetail | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [dialog, setDialog] = useState<'dismiss' | 'resolve' | null>(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      setDetail(await kbSweepsApi.finding(projectId, findingId));
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    }
  }, [projectId, findingId, onError]);

  useEffect(() => {
    void load();
  }, [load]);

  // A card being fixed keeps polling until the runs land and the worker settles it.
  const fixing = detail?.status === 'FIXING';
  useEffect(() => {
    if (!fixing) return;
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [fixing, load]);

  async function run(fn: () => Promise<unknown>) {
    try {
      await fn();
      await load();
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    }
  }

  if (!detail) return <p className="text-sm text-ink-faint">Loading…</p>;
  const gone = new Set(detail.pagesNow.filter((p) => !p.exists).map((p) => p.nodeId));

  return (
    <div className="space-y-5">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-[11px] text-ink-muted hover:text-ink">
        <ChevronLeft size={12} /> Back
      </button>

      <div>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <code className="text-[11px] text-ink-muted">{detail.shortId}</code>
          <Badge variant={SEVERITY_VARIANT[detail.severity]}>{KB_SWEEP_SEVERITY_LABELS[detail.severity]}</Badge>
          <Badge variant="default" title={KB_SWEEP_FINDING_KIND_DESCRIPTIONS[detail.kind]}>{KB_SWEEP_FINDING_KIND_LABELS[detail.kind]}</Badge>
          <Badge variant={STATUS_VARIANT[detail.status]}>{KB_SWEEP_FINDING_STATUS_LABELS[detail.status]}</Badge>
          <span className="ml-auto flex items-center gap-2 text-[10px] text-ink-faint">
            {detail.fixUsage.runs > 0 && (
              <span className="inline-flex items-center gap-1 tabular-nums" title={`Fix runs: ${describeRunUsage(detail.fixUsage)}`}>
                <Wrench size={9} /> {detail.fixUsage.runs} fix run{detail.fixUsage.runs === 1 ? '' : 's'} · {formatRunCost(detail.fixUsage.costUsd)}
              </span>
            )}
            <span title={`First seen ${formatDate(detail.firstSeenAt)}; seen by ${detail.seenCount} sweep(s)`}>
              first seen {timeAgo(detail.firstSeenAt)}{detail.seenCount > 1 ? ` · confirmed ${detail.seenCount - 1}×` : ''}
            </span>
          </span>
        </div>
        <h2 className="text-base font-semibold text-ink">{detail.title}</h2>
      </div>

      <section className="rounded-lg border border-border bg-card px-5 py-4 space-y-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-ink-faint mb-1.5">Pages</p>
          <ul className="space-y-1">
            {detail.pages.map((page) => {
              const slug = projectSlugFor(page.projectId);
              const missing = gone.has(page.nodeId);
              return (
                <li key={page.nodeId} className="flex items-center gap-2 text-xs">
                  {slug && !missing ? (
                    <Link to={`/p/${slug}/kb/${page.path}`} className="text-orange-600 hover:underline truncate">{page.address}</Link>
                  ) : (
                    <code className={cn('truncate', missing ? 'text-ink-faint line-through' : 'text-ink-secondary')}>{page.address}</code>
                  )}
                  <span className="text-ink-faint truncate">— {page.title}</span>
                  {missing && <span className="text-[10px] text-ink-faint">(no longer exists)</span>}
                </li>
              );
            })}
          </ul>
          {(detail.suggestedProjectName || detail.suggestedPath) && (
            <p className="mt-2 text-xs text-ink-secondary">
              <span className="text-ink-faint">Belongs in:</span> {detail.suggestedProjectName ?? ''}{detail.suggestedPath ? <code className="ml-1 text-[11px]">{detail.suggestedPath}</code> : null}
            </p>
          )}
        </div>

        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-ink-faint mb-1.5">What the sweep found</p>
          <Markdown className="text-sm" components={compactMarkdownComponents}>{detail.summary}</Markdown>
        </div>

        {detail.evidence.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-ink-faint mb-1.5">Evidence, verbatim</p>
            <ul className="space-y-1.5">
              {detail.evidence.map((quote, i) => (
                <li key={i} className="rounded border border-amber-300/60 bg-amber-50/60 px-3 py-1.5 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-200">“{quote}”</li>
              ))}
            </ul>
          </div>
        )}

        {detail.proposedAction && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-ink-faint mb-1.5">The agent would</p>
            <p className="text-xs text-ink-secondary whitespace-pre-wrap">{detail.proposedAction}</p>
          </div>
        )}

        {detail.status === 'DISMISSED' && (
          <p className="text-xs text-ink-muted"><Ban size={11} className="inline mr-1" />Dismissed {detail.dismissedAt ? timeAgo(detail.dismissedAt) : ''}{detail.dismissNote ? ` — ${detail.dismissNote}` : ''}</p>
        )}
        {detail.status === 'RESOLVED' && (
          <p className="text-xs text-ink-muted"><Check size={11} className="inline mr-1" />Resolved {detail.resolvedAt ? timeAgo(detail.resolvedAt) : ''} by {detail.resolvedBy ?? '—'}{detail.resolveNote ? ` — ${detail.resolveNote}` : ''}</p>
        )}
      </section>

      {/* The thread: what people said, what the agent did about it. */}
      <section className="rounded-lg border border-border bg-card">
        <div className="px-5 py-3 border-b border-border-subtle flex items-center justify-between">
          <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-ink-faint">Thread</p>
          {detail.fixRuns.length > 0 && (
            <span className="inline-flex items-center gap-2 text-[10px] text-ink-faint">
              {detail.fixRuns.length} fix run{detail.fixRuns.length === 1 ? '' : 's'}
              <RunCostChip usage={detail.fixUsage} />
            </span>
          )}
        </div>
        <div className="px-5 py-4 space-y-3">
          {detail.messages.length === 0 && <p className="text-xs text-ink-faint">Nobody has said anything yet.</p>}
          {detail.messages.map((message) => (
            <div key={message.id} className={cn('flex gap-2.5', message.role === 'USER' ? '' : '')}>
              <span className={cn('mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full', message.role === 'USER' ? 'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300' : message.role === 'AGENT' ? 'bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300' : 'bg-surface-muted text-ink-faint')}>
                {message.role === 'USER' ? <User size={11} /> : message.role === 'AGENT' ? <Bot size={11} /> : <Clock size={11} />}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] text-ink-faint mb-0.5">
                  {message.role === 'USER' ? 'You' : message.role === 'AGENT' ? 'The agent' : 'System'} · {timeAgo(message.createdAt)}
                </p>
                {message.role === 'AGENT' ? (
                  <Markdown className="text-xs" components={compactMarkdownComponents}>{message.body}</Markdown>
                ) : (
                  <p className="text-xs text-ink-secondary whitespace-pre-wrap">{message.body}</p>
                )}
                {message.runIds.length > 0 && (
                  <p className="mt-1 flex flex-wrap gap-2 text-[10px]">
                    {message.runIds.map((runId) => {
                      const run = detail.fixRuns.find((r) => r.id === runId);
                      return (
                        <span key={runId} className="inline-flex items-center gap-1.5">
                          <Link to={`/activity?run=${runId}`} className="inline-flex items-center gap-1 text-orange-600 hover:underline">
                            {run && LIVE.has(run.status) && <BreathingDot size={6} />}
                            {run?.projectName ?? 'run'}{run ? ` · ${run.status.toLowerCase()}` : ''}
                          </Link>
                          {run && <RunCostChip usage={runUsageOf(run)} />}
                        </span>
                      );
                    })}
                  </p>
                )}
              </div>
            </div>
          ))}

          {fixing && (
            <div className="flex items-center gap-2 rounded border border-border-subtle bg-surface px-3 py-2 text-[11px] text-ink-secondary">
              <BreathingDot size={7} />
              A fix run is rewriting these pages from your instruction. The card closes itself when it lands; a change comes to KB Review only if it conflicts, holds sensitive information, or the agent was unsure.
            </div>
          )}
        </div>

        {canRun ? (
          <div className="px-5 pb-4">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              disabled={fixing || sending}
              placeholder={fixing ? 'Wait for the fix in flight to land before sending more.' : 'Tell the agent what to do — “merge B into A and leave a pointer”, “move this to Heist under engagements/”, “the dates in A are right; fix B”…'}
              className="w-full text-xs"
            />
            <div className="mt-2 flex items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                disabled={fixing || sending || draft.trim().length < 4}
                onClick={async () => {
                  setSending(true);
                  await run(async () => {
                    await kbSweepsApi.message(projectId, findingId, draft.trim());
                    setDraft('');
                  });
                  setSending(false);
                }}
              >
                {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send to the agent
              </Button>
              <span className="flex-1" />
              {(detail.status === 'OPEN' || detail.status === 'FIXED') && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => { setNote(''); setDialog('resolve'); }} title="The problem no longer holds; nothing for the agent to do">
                    <Check size={13} /> Resolve
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setNote(''); setDialog('dismiss'); }} title="Not a problem — the next sweep will leave it dismissed">
                    <X size={13} /> Dismiss
                  </Button>
                </>
              )}
              {(detail.status === 'DISMISSED' || detail.status === 'RESOLVED') && (
                <Button variant="ghost" size="sm" onClick={() => run(() => kbSweepsApi.reopen(projectId, findingId))}>
                  <RotateCcw size={13} /> Reopen
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="px-5 pb-4 flex items-center gap-2">
            <AlertTriangle size={12} className="text-ink-faint" />
            <span className="text-[11px] text-ink-faint">Your account can read cards but not set the agent running on them.</span>
            <span className="flex-1" />
            {detail.status === 'OPEN' && (
              <>
                <Button variant="ghost" size="sm" onClick={() => { setNote(''); setDialog('resolve'); }}><Check size={13} /> Resolve</Button>
                <Button variant="ghost" size="sm" onClick={() => { setNote(''); setDialog('dismiss'); }}><X size={13} /> Dismiss</Button>
              </>
            )}
          </div>
        )}
      </section>

      <Dialog open={dialog !== null} onClose={() => setDialog(null)} title={dialog === 'dismiss' ? `Dismiss ${detail.shortId}` : `Resolve ${detail.shortId}`}>
        <div className="space-y-3">
          <p className="text-xs text-ink-muted">
            {dialog === 'dismiss'
              ? 'Not a problem. The card stays dismissed even if the next sweep flags the same pages, and your note is shown to the agent so it does not re-raise the point.'
              : 'The problem no longer holds — fixed by hand, or judged fine on a second look. Recorded on the thread.'}
          </p>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Why (optional)" className="w-full text-xs" />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setDialog(null)}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              onClick={async () => {
                const which = dialog;
                setDialog(null);
                await run(() => (which === 'dismiss' ? kbSweepsApi.dismiss(projectId, findingId, note.trim() || undefined) : kbSweepsApi.resolve(projectId, findingId, note.trim() || undefined)));
              }}
            >
              {dialog === 'dismiss' ? 'Dismiss' : 'Resolve'}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

// ─── Cost ───────────────────────────────────────────────────────────────────

/**
 * What the last month cost, under the tabs — one line, two figures, because they answer to two
 * different levers: the sweeps' own spend moves with how often the schedule fires, the fix runs'
 * with how many cards a person sends back. Hidden until there is something to show.
 */
function SpendLine({ spend }: { spend: NonNullable<KbSweepCounts['spend']> }) {
  const part = (label: string, total: KbSweepUsageTotal) =>
    total.runs === 0 ? null : (
      <span title={describeRunUsage(total)} className="tabular-nums">
        {formatRunCost(total.costUsd)} on {total.runs} {label}{total.runs === 1 ? '' : 's'}
      </span>
    );
  return (
    <div className="flex items-center gap-1.5 border-b border-border-subtle px-4 py-1.5 text-[10px] text-ink-faint">
      <Coins size={9} className="flex-shrink-0" />
      <span>Last {spend.days} days ·</span>
      {part('sweep', spend.sweeps)}
      {spend.sweeps.runs > 0 && spend.fixes.runs > 0 && <span>·</span>}
      {part('fix run', spend.fixes)}
    </div>
  );
}

/**
 * The sweep's bill, and the bill of what came after it.
 *
 * Three figures rather than one: the sweep's own spend (climbing live while it runs — each model call
 * lands on the row as it happens), the fix runs its cards dispatched (which is the cost of acting on
 * what it found, not of finding it), and the two together. Tokens sit beside the money for the reason
 * `RunCostStats` gives: prices age, token counts are what the run actually did.
 */
function SweepCostStrip({ sweep, fixUsage, totalUsage, live }: { sweep: KbSweep; fixUsage: KbSweepUsageTotal; totalUsage: RunUsage; live: boolean }) {
  const own = runUsageOf(sweep);
  const cell = (label: string, value: string, title?: string) => (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-ink-faint">{label}</p>
      <p className="text-ink tabular-nums" title={title}>{value}</p>
    </div>
  );
  return (
    <div className="grid grid-cols-3 gap-4 rounded-lg border border-border bg-card px-5 py-4 text-sm sm:grid-cols-6">
      {cell('Sweep cost', own.modelCalls > 0 ? `${formatRunCost(own.costUsd)}${live ? ' …' : ''}` : live ? '…' : '—', describeRunUsage(own))}
      {cell('Tokens', runUsageTokens(own) > 0 ? formatRunTokens(runUsageTokens(own)) : '—', describeRunUsage(own))}
      {cell('Fix runs', fixUsage.runs > 0 ? `${fixUsage.runs} · ${formatRunCost(fixUsage.costUsd)}` : '—', fixUsage.runs > 0 ? describeRunUsage(fixUsage) : 'No card from this sweep has been sent for a fix yet')}
      {cell('Total', totalUsage.modelCalls > 0 ? formatRunCost(totalUsage.costUsd) : '—', describeRunUsage(totalUsage))}
      {cell('Pages read', String(sweep.pagesRead))}
      {cell('Tool calls', String(sweep.toolCalls))}
    </div>
  );
}

// ─── The daily schedule ─────────────────────────────────────────────────────

function SettingsPanel({ settings, projectId, onSaved, onError }: { settings: KbSweepSettings; projectId: string; onSaved: (next: KbSweepSettings) => void; onError: (m: string) => void }) {
  const [draft, setDraft] = useState<KbSweepSettings>(settings);
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(settings), [settings]);

  return (
    <div className="rounded-md border border-border bg-card px-3 py-2.5 space-y-2 text-xs">
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={draft.sweepMode === 'DAILY'} onChange={(e) => setDraft({ ...draft, sweepMode: e.target.checked ? 'DAILY' : 'OFF' })} className="h-3.5 w-3.5 accent-orange-500" />
        <span className="text-ink-secondary">Sweep this scope every day</span>
      </label>
      <div className="flex gap-2">
        <Input type="time" value={draft.sweepTimeOfDay ?? ''} onChange={(e) => setDraft({ ...draft, sweepTimeOfDay: e.target.value || null })} className="w-28 py-1 text-xs" disabled={draft.sweepMode !== 'DAILY'} />
        <Input value={draft.sweepTimezone} onChange={(e) => setDraft({ ...draft, sweepTimezone: e.target.value })} className="flex-1 min-w-0 py-1 text-xs" placeholder="America/Los_Angeles" disabled={draft.sweepMode !== 'DAILY'} />
      </div>
      <Textarea
        value={draft.sweepInstruction ?? ''}
        onChange={(e) => setDraft({ ...draft, sweepInstruction: e.target.value || null })}
        rows={2}
        placeholder="Standing brief for every scheduled sweep (optional)"
        className="w-full text-xs"
        disabled={draft.sweepMode !== 'DAILY'}
      />
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={saving}
          onClick={async () => {
            setSaving(true);
            try {
              onSaved(await kbSweepsApi.updateSettings(projectId, { sweepMode: draft.sweepMode, sweepTimeOfDay: draft.sweepTimeOfDay, sweepTimezone: draft.sweepTimezone, sweepInstruction: draft.sweepInstruction }));
            } catch (err) {
              onError(err instanceof Error ? err.message : String(err));
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
        </Button>
        {settings.lastSweptAt && <span className="text-[10px] text-ink-faint">last scheduled fire {timeAgo(settings.lastSweptAt)}</span>}
      </div>
    </div>
  );
}
